import React, { useCallback, useEffect, useMemo } from "react";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { fetchEntitiesPaginated, fetchEntityActions, fetchEntitySummary, fetchWarehouseReturnErrorTypes, fetchWarehouseReturnSources, fetchWarehouseReturnStatuses } from "~/services";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";
import { useSearchStringState, useTablePageState, useTableSizeState } from "~/hooks";
import { useTableSortQuery } from "~/components/table/useTableSortQuery.jsx";
import { COLORS } from "rylak-common/src/constants";
import { ENTITY_TYPE, QUERY_MAP, WAREHOUSE_RETURN_STATUS, WORKER_STATUS } from "~/constants";
import WarehouseReturnStatusChip, { getWarehouseReturnSourceLabel, WAREHOUSE_RETURN_SOURCE_MAP } from "~/components/chips/statusChips/WarehouseReturnStatusChip.jsx";
import TurnrTableSmart, { TurnrTableCell, TurnrTableRow } from "~/components/table/TurnrTableSmart.jsx";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import TurnrDropdownSelect from "~/components/input/TurnrDropdownSelect.jsx";
import { getTableHeadCustomer, TableCellCustomer } from "~/components/table/cells/TableCellCustomer.jsx";
import { getTableHeadDateTime, TableCellDateTime } from "~/components/table/cells/TableCellDateTime.jsx";
import TurnrCheckbox from "~/components/input/TurnrCheckbox";
import { getTableHeadBrand, TableCellBrand } from "~/components/table/cells/TableCellBrand.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import { serviceIsLoading, useServiceHook } from "rylak-common";
import WmsLogoIcon from "~/components/icons/WmsLogoIcon.jsx";
import CountryCodeSelect from "~/components/input/selects/CountrySelect.jsx";
import CarrierIcon from "rylak-common/src/icons/CarrierIcon.jsx";
import { getCarrierData } from "~/cores/appCore.js";
import { extractCreateEntityAction } from "~/components/entityAction/utils/entityActionsCore.js";
import EntityActionButton from "~/components/entityAction/EntityActionButton.jsx";
import { getTableHeadActions, TableCellActions } from "~/components/table/cells/TableCellActions.jsx";
import { getTableHeadOrderId, TableCellOrderId } from "~/components/table/cells/TableCellOrderId.jsx";
import { TableCellUser } from "~/components/table/cells/TableCellUser.jsx";
import { useHighlightTextState } from "~/components/highlightText/HighlightText.jsx";
import SearchInput from "~/components/input/inputs/SearchInput.jsx";
import { CustomImageNew } from "rylak-common/src/image/CustomImage.jsx";
import TurnrDropdownSelectChip from "~/components/input/TurnrDropdownSelectChip.jsx";
import TurnrModal from "~/components/modals/TurnrModal.jsx";
import { EntityActionStatusChip } from "~/components/entityAction/EntityActionStatusChip.jsx";
import { TableCellReturnItems } from "~/components/table/cells/TableCellReturnItems.jsx";
import TableCellShippingMethod from "~/components/table/cells/TableCellShippingMethod.jsx";
import useSelectListItemModalState from "~/components/modals/useSelectListItemModalState.jsx";
import { WarehouseReportInfoModalContent } from "./WarehouseReportInfoModalContent";

export function WarehouseSourceCellContent({ appProps, warehouseReport }) {
    return (
        <div
            style={{
                whiteSpace: "nowrap",
                fontSize: "11px",
                color: COLORS.WEAK_TEXT,
            }}
        >
            {warehouseReport?.wmsType ? (
                <div
                    style={{
                        position: "relative",
                        width: "75px",
                        height: "20px",
                    }}
                >
                    <WmsLogoIcon type={warehouseReport?.wmsType} />
                </div>
            ) : warehouseReport?.shipmentCarrier ? (
                <div
                    style={{
                        position: "relative",
                        width: "75px",
                        height: "20px",
                    }}
                >
                    <CarrierIcon carrierData={getCarrierData(appProps, { id: warehouseReport?.shipmentCarrier })} />
                </div>
            ) : (
                getWarehouseReturnSourceLabel(warehouseReport?.source)
            )}
        </div>
    );
}

// export function WarehouseReturnStatusCellContent({ appProps, warehouseReport, onSuccess }) {
//     const statusChipContent = <WarehouseReturnStatusChip appProps={appProps} entity={warehouseReport} size={"small"} showLabel={true} onSuccess={onSuccess} />;
//     return warehouseReport?.warehouseReturnStatus === WAREHOUSE_RETURN_STATUS.AWAITING_MERCHANT_CHECK ? (
//         <HandleWarehouseComplaintSmartButton appProps={appProps} warehouseReport={warehouseReport}>
//             {statusChipContent}
//         </HandleWarehouseComplaintSmartButton>
//     ) : (
//         statusChipContent
//     );
// }

function getTableHeadData({ appProps }) {
    return {
        items: [
            getTableHeadBrand({ id: "order.merchantBrand.name", appProps, hide: "xxl" }),
            {
                id: "reporter",
                label: "Reporter",
                hide: "xl",
                sortable: false,
            },
            {
                id: "reviewer",
                label: "Reviewer",
                hide: "xl",
                sortable: false,
            },
            getTableHeadDateTime({
                sortable: true,
                hide: "lg",
                defaultSortedBy: true,
            }),
            getTableHeadOrderId({ sortable: false }),
            {
                id: "return-shipment",
                label: "Return/Shipment",
                hide: "sm",
                sortable: false,
            },
            getTableHeadCustomer({ hide: "lg", sortable: false }),
            {
                id: "nrOfReturns",
                label: "Items",
                hide: "xl",
                sortable: false,
            },

            {
                id: "status",
                label: "Status",
                hide: "",
                sortable: false,
            },
            getTableHeadActions(),
        ],
    };
}

function ShippingMethodWrappingComponent({ children, orderReturn, shipment }) {
    return (
        <div
            style={{
                marginLeft: "10px",
                minHeight: "30px",
                display: "flex",
                alignItems: "center",
                gap: "20px",
            }}
        >
            <div
                style={{
                    fontSize: "11px",
                }}
                className={"muted"}
            >
                {shipment ? "Shipment" : orderReturn ? "Return" : ""}
            </div>
            {children}
        </div>
    );
}

function ReturnItemsWrappingComponent({ children }) {
    return <div style={{ minWidth: "73px" }}>{children}</div>;
}

export default function WarehouseIncomingItemsContent(props) {
    const { appProps, hideStatusField, hideErrorField, defaultStatus } = props;

    let tableHeadData = useMemo(() => getTableHeadData({ appProps }), [appProps]);
    let tableSortData = useTableSortQuery(tableHeadData);
    const { sort } = tableSortData;
    const [searchString, setSearchString] = useSearchStringState();
    const [page, setPage] = useTablePageState();
    const [size] = useTableSizeState({ defaultValue: 25 });
    const [returnSource, setReturnSource] = useQueryState({
        key: QUERY_MAP.WAREHOUSE_RETURN_SOURCE,
    });

    const [returnStatus, setReturnStatus] = useQueryState({
        key: QUERY_MAP.RETURN_STATUS,
        defaultValue: defaultStatus,
    });

    const [selectedCountryCode, setSelectedCountryCode] = useQueryState({
        key: QUERY_MAP.COUNTRY_CODE,
    });

    const [filterError, setFilterError] = useQueryState({
        key: QUERY_MAP.FILTER_ERROR,
    });
    const [errorType, setErrorType] = useQueryState({
        key: QUERY_MAP.WAREHOUSE_RETURN_ERROR_TYPE,
    });

    let { serviceData: warehouseReportsServiceData, fetchServiceData: warehouseReportsServiceFetchFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntitiesPaginated,
    });
    let triggerReturnsFetch = useCallback(() => {
        warehouseReportsServiceFetchFn(
            {
                entityType: ENTITY_TYPE.WAREHOUSE_RETURN,
                page,
                size,
                sort,
                source: returnSource,
                status: returnStatus,
                countryCode: selectedCountryCode,
                workerStatus: filterError ? WORKER_STATUS.ERROR : undefined,
                errorType,
                query: searchString,
            },
            { silentFetch: true },
        );
    }, [page, size, sort, returnSource, returnStatus, selectedCountryCode, filterError, errorType, searchString]);
    let { serviceData: returnSourcesService } = useServiceHook({
        appProps,
        fetchFn: fetchWarehouseReturnSources,
    });
    let { serviceData: warehouseReportStatusesService } = useServiceHook({
        appProps,
        fetchFn: fetchWarehouseReturnStatuses,
    });
    let { serviceData: warehouseReportErrorTypesService } = useServiceHook({
        appProps,
        fetchFn: fetchWarehouseReturnErrorTypes,
    });
    let { serviceData: warehouseReportsActions, fetchServiceData: fetchWarehouseReturnsActionsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntityActions,
    });

    let { serviceData: reportSummaryServiceData, fetchServiceData: fetchReportSummaryServiceFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntitySummary,
    });

    const [reportModalOpen, selectedReport, setSelectedReport] = useSelectListItemModalState();

    useEffect(() => {
        if (reportModalOpen && selectedReport) {
            fetchReportSummaryServiceFn({
                entityType: ENTITY_TYPE.WAREHOUSE_RETURN,
                entityId: selectedReport?.id,
            });
        }
    }, [reportModalOpen, selectedReport]);

    let triggerWarehouseReturnsActionsFetch = useCallback(() => {
        fetchWarehouseReturnsActionsFn(
            {
                entityType: ENTITY_TYPE.WAREHOUSE_RETURN,
            },
            { silentFetch: true },
        );
    }, []);
    const [createAction, filteredActions] = extractCreateEntityAction(warehouseReportsActions?.data);

    useEffect(() => {
        triggerReturnsFetch();
        triggerWarehouseReturnsActionsFetch();
    }, [triggerReturnsFetch, triggerWarehouseReturnsActionsFetch]);

    const onReturnStatusChange = useCallback(
        (value) => {
            setPage(0);
            setReturnStatus(value);
        },
        [setPage, setReturnStatus],
    );

    const highlightText = useHighlightTextState({ searchString, serviceData: warehouseReportsServiceData });

    return (
        <TurnrCard>
            <TurnrModal open={reportModalOpen} onClose={setSelectedReport}>
                <WarehouseReportInfoModalContent
                    appProps={appProps}
                    warehouseReport={selectedReport}
                    warehouseReportSummary={reportSummaryServiceData?.data}
                    loading={serviceIsLoading(reportSummaryServiceData)}
                />
            </TurnrModal>
            <TurnrCardContent>
                <TurnrGrid container spacing={3} alignItems={"center"}>
                    <TurnrGrid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SearchInput
                            value={searchString}
                            onChange={(v) => {
                                setPage(0);
                                setSearchString(v);
                            }}
                            fullWidth={true}
                        />
                    </TurnrGrid>
                    <TurnrGrid item xs={12} md={"auto"}>
                        {returnSourcesService?.data && (
                            <TurnrDropdownSelect
                                value={returnSource}
                                size={"small"}
                                tight={true}
                                fullWidth={true}
                                label={"Reporter"}
                                onChange={(v) => {
                                    setPage(0);
                                    setReturnSource(v);
                                    setErrorType(null);
                                }}
                                renderOption={(value) => {
                                    const icon = WAREHOUSE_RETURN_SOURCE_MAP[value]?.icon;
                                    const name = WAREHOUSE_RETURN_SOURCE_MAP[value]?.label || value;
                                    return (
                                        <div
                                            style={{
                                                height: "100%",
                                                width: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            {icon ? (
                                                <div
                                                    style={{
                                                        width: "90px",
                                                        height: "20px",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <CustomImageNew fill src={icon} alt={name} draggable={false} />
                                                </div>
                                            ) : (
                                                name
                                            )}
                                        </div>
                                    );
                                }}
                                options={returnSourcesService?.data}
                                selectNoneComponent={"All"}
                            />
                        )}
                    </TurnrGrid>
                    {!hideStatusField && (
                        <TurnrGrid item xs={12} md={"auto"}>
                            {warehouseReportStatusesService?.data && (
                                <TurnrDropdownSelectChip
                                    value={returnStatus}
                                    fullWidth={true}
                                    onChange={onReturnStatusChange}
                                    StatusChipComponent={WarehouseReturnStatusChip}
                                    options={warehouseReportStatusesService?.data}
                                />
                            )}
                        </TurnrGrid>
                    )}
                    <TurnrGrid item xs={12} md={"auto"}>
                        <CountryCodeSelect
                            label={"Customer country"}
                            fullWidth
                            value={selectedCountryCode}
                            onChange={(v) => {
                                setPage(0);
                                setSelectedCountryCode(v);
                            }}
                            includeCountryGroups={false}
                            includeGlobal={true}
                        />
                    </TurnrGrid>
                    {!hideErrorField && (
                        <TurnrGrid item xs={12} md={"auto"}>
                            <TurnrCheckbox
                                checked={!!filterError}
                                size={"small"}
                                label={"Error"}
                                name="filterError"
                                onChange={(v) => {
                                    setPage(0);
                                    setFilterError(v || undefined);
                                }}
                            />
                        </TurnrGrid>
                    )}
                    {returnStatus === WAREHOUSE_RETURN_STATUS.ERROR ? (
                        <TurnrGrid item>
                            {warehouseReportErrorTypesService?.data && (
                                <TurnrDropdownSelect
                                    value={errorType}
                                    size={"small"}
                                    onChange={(v) => {
                                        setPage(0);
                                        setErrorType(v);
                                    }}
                                    label={"Error type"}
                                    options={warehouseReportErrorTypesService?.data}
                                    selectNoneComponent={"All"}
                                />
                            )}
                        </TurnrGrid>
                    ) : null}
                    {createAction && (
                        <TurnrGrid item>
                            <EntityActionButton
                                appProps={appProps}
                                entityAction={createAction}
                                onSuccess={triggerReturnsFetch}
                                buttonProps={{
                                    variant: "outlined",
                                    size: "medium",
                                }}
                            />
                        </TurnrGrid>
                    )}
                    <TurnrGrid item>
                        <EntityActionsDotsMenu hideIfEmpty={true} appProps={appProps} entityType={ENTITY_TYPE.WAREHOUSE_RETURN} entityActions={filteredActions} />
                    </TurnrGrid>
                </TurnrGrid>
            </TurnrCardContent>
            <div>
                <TurnrTableSmart cellSize={"small"} tableHeadData={tableHeadData} serviceData={warehouseReportsServiceData} tableSortData={tableSortData} page={page} setPage={setPage} size={size}>
                    {warehouseReportsServiceData?.data?.content?.map((warehouseReport) => {
                        const order = warehouseReport?.order;
                        const orderReturn = warehouseReport?.orderReturn;
                        const shipment = warehouseReport?.shipment;
                        return (
                            <TurnrTableRow
                                key={warehouseReport?.id}
                                style={{ height: "50px" }}
                                onClick={() => setSelectedReport(warehouseReport)}
                                // onClick={onOrderRowClick && (() => onOrderRowClick(order))}
                            >
                                <TableCellBrand appProps={appProps} entity={warehouseReport} />
                                {warehouseReport?.user ? (
                                    <TableCellUser appProps={appProps} entity={warehouseReport} />
                                ) : (
                                    <TurnrTableCell>
                                        <WarehouseSourceCellContent appProps={appProps} warehouseReport={warehouseReport} />
                                    </TurnrTableCell>
                                )}
                                {warehouseReport?.reviewedBy ? <TableCellUser appProps={appProps} user={warehouseReport?.reviewedBy} /> : <div></div>}
                                <TableCellDateTime dateTime={warehouseReport?.createdAt} />
                                <TableCellOrderId order={order} foreignOrderId={warehouseReport?.foreignOrderId} highlightText={highlightText} />
                                <TableCellShippingMethod
                                    appProps={appProps}
                                    darkerHover={true}
                                    rowClick={true}
                                    includeNumbers={true}
                                    showShippingMethod={false}
                                    highlightText={highlightText}
                                    WrapperComponent={ShippingMethodWrappingComponent}
                                    shipment={shipment}
                                    orderReturn={orderReturn}
                                    tight={false}
                                />
                                <TableCellCustomer order={order} highlightText={highlightText} entityAddress={order?.address} />
                                <TableCellReturnItems darkerHover={true} WrapperComponent={ReturnItemsWrappingComponent} appProps={appProps} warehouseReport={warehouseReport} />
                                <TurnrTableCell>
                                    <EntityActionStatusChip size="small" appProps={appProps} entity={warehouseReport} onSuccess={triggerReturnsFetch} StatusChipComponent={WarehouseReturnStatusChip} />
                                </TurnrTableCell>
                                <TableCellActions appProps={appProps}>
                                    <EntityActionsDotsMenu appProps={appProps} entity={warehouseReport} onSuccess={triggerReturnsFetch} />
                                </TableCellActions>
                            </TurnrTableRow>
                        );
                    })}
                </TurnrTableSmart>
            </div>
        </TurnrCard>
    );
}
